import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
    Upload, Play, Pause, RotateCcw, Settings, Languages, Plus, Minus,
    ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Edit3, Smile, Image as ImageIcon, XCircle,
    Download, BarChart3, TrendingUp
} from 'lucide-react';
// Make sure to install fit-file-parser: npm install fit-file-parser
import FitParser from 'fit-file-parser';

// --- ПЕРЕВОДЫ ---
const translations = {
    ru: {
        title: "Визуализатор GPS Треков", loadGpx: "Загрузить GPX/FIT", play: "Старт", pause: "Пауза",
        reset: "Сброс", clearAll: "Очистить всё", settings: "Настройки", trackSettings: "Треки",
        mapSettings: "Карта", playbackSettings: "Воспроизведение", trackOffsetX: "Смещение X:",
        trackOffsetY: "Смещение Y:", metrics: "Метрики:", speed: "Скорость", elevation: "Высота",
        heartRate: "Пульс", cadence: "Каденс", trackVisualization: "Визуализация трека:",
        uniform: "Обычная", playbackSpeed: "Скорость:", maxTrackWidth: "Ширина трека:",
        zoom: "Масштаб:", relTime: "Отн. время:", metricSpeedLabel: "СКОРОСТЬ",
        metricElevationLabel: "ВЫСОТА", metricHeartRateLabel: "ПУЛЬС", metricCadenceLabel: "КАДЕНС",
        speedUnit: "км/ч", hrUnit: "уд/мин", elevationUnit: "м", cadenceUnit: "об/мин",
        languageSwitchToEN: "EN", languageSwitchToRU: "РУС", initialLoadMessage: "Выберите один или несколько файлов GPX или FIT.",
        initialLoadSubMessage: "Их можно извлечь из вашего фитнес-приложения или велокомпьютера.",
        loadInitialGpx: "ЗАГРУЗИТЬ GPX/FIT ТРЕК(И)", editNameTooltip: "Редактировать имя",
        editAvatarTooltip: "Изменить аватар", chooseEmoji: "Выбрать Emoji", uploadImage: "Загрузить фото",
        currentAvatar: "Аватар:", enterNewName: "Введите новое имя для трека:", close: "Закрыть",
        selectAnEmoji: "Выберите Emoji:", uploadInstructions: "Перетащите файл сюда, или кликните для выбора",
        pasteImageSupport: "(Вставка из буфера пока не поддерживается)", removeCustomAvatar: "Сбросить аватар",
        generalSettings: "Общие", visualizationSettings: "Визуализация", exportFrame: "Экспорт кадра",
        statistics: "Статистика", totalDistance: "Общая дистанция", avgSpeed: "Средняя скорость",
        maxSpeed: "Макс. скорость", elevationGain: "Набор высоты", duration: "Длительность"
    },
    en: {
        title: "GPS Track Visualizer", loadGpx: "Load GPX/FIT", play: "Play", pause: "Pause",
        reset: "Reset", clearAll: "Clear All", settings: "Settings", trackSettings: "Tracks",
        mapSettings: "Map", playbackSettings: "Playback", trackOffsetX: "Offset X:",
        trackOffsetY: "Offset Y:", metrics: "Metrics:", speed: "Speed", elevation: "Elevation",
        heartRate: "Heart Rate", cadence: "Cadence", trackVisualization: "Track Visualization:",
        uniform: "Uniform", playbackSpeed: "Speed:", maxTrackWidth: "Track Width:", zoom: "Zoom:",
        relTime: "Rel. Time:", metricSpeedLabel: "SPEED", metricElevationLabel: "ELEVATION",
        metricHeartRateLabel: "HEART RATE", metricCadenceLabel: "CADENCE", speedUnit: "km/h",
        hrUnit: "bpm", elevationUnit: "m", cadenceUnit: "rpm", languageSwitchToEN: "EN",
        languageSwitchToRU: "RU", initialLoadMessage: "Select one or more GPX or FIT files.",
        initialLoadSubMessage: "You can export them from your fitness app or bike computer.",
        loadInitialGpx: "LOAD GPX/FIT TRACK(S)", editNameTooltip: "Edit name", editAvatarTooltip: "Change avatar",
        chooseEmoji: "Choose Emoji", uploadImage: "Upload Photo", currentAvatar: "Avatar:",
        enterNewName: "Enter new name for the track:", close: "Close", selectAnEmoji: "Select an Emoji:",
        uploadInstructions: "Drag 'n' drop an image here, or click to select",
        pasteImageSupport: "(Pasting from clipboard is not yet supported)", removeCustomAvatar: "Reset avatar",
        generalSettings: "General", visualizationSettings: "Visualization", exportFrame: "Export Frame",
        statistics: "Statistics", totalDistance: "Total Distance", avgSpeed: "Average Speed",
        maxSpeed: "Max Speed", elevationGain: "Elevation Gain", duration: "Duration"
    }
};

const defaultAvatarsEmojiList = [
    '🚴', '🏃', '🚶', '🚶‍♀️', '🏃‍♂️', '⛰️', '🌲', '🏁', '🎯', '😀', '😎', '🚀', '🌟', '👍', '💡', '🌍',
    '🚲', '⚽️', '🏀', '🎾', '🐞', '🪲', '🐝', '🐋', '🐳', '🌞', '🌚', '⭐️', '🦀', '🦄', '🐼', '🦁'
];

// Современные стили с анимациями
const modernStyles = {
    container: {
        maxWidth: '1400px', margin: '0 auto', padding: '20px', color: 'white',
        minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    button: {
        backgroundColor: '#3b82f6', color: 'white', padding: '10px 16px', borderRadius: '8px',
        border: 'none', cursor: 'pointer', margin: '2px', display: 'inline-flex',
        alignItems: 'center', gap: '8px', fontSize: '0.9em', fontWeight: '500',
        transition: 'all 0.2s ease-in-out', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    buttonSmall: { padding: '6px 10px', fontSize: '0.8em' },
    buttonDisabled: { backgroundColor: '#6b7280', cursor: 'not-allowed', transform: 'none' },
    card: {
        backgroundColor: '#1f2937', padding: '20px', borderRadius: '12px', marginBottom: '20px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: '1px solid #374151'
    },
    cardGradient: {
        background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
        backdropFilter: 'blur(10px)'
    },
    input: {
        backgroundColor: '#374151', color: 'white', padding: '10px 12px', borderRadius: '8px',
        border: '1px solid #4b5563', fontSize: '0.9em', transition: 'border-color 0.2s ease'
    },
    floatingButton: {
        position: 'fixed', bottom: '24px', right: '24px', backgroundColor: '#3b82f6',
        color: 'white', border: 'none', borderRadius: '50%', width: '56px', height: '56px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        boxShadow: '0 8px 24px rgba(59, 130, 246, 0.4)', transition: 'all 0.3s ease',
        zIndex: 1000
    },
    statsCard: {
        backgroundColor: '#1f2937', padding: '16px', borderRadius: '10px',
        border: '1px solid #374151', display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: '8px', minWidth: '120px'
    },
    animatedTrackCard: {
        border: '1px solid', borderRadius: '12px', padding: '12px',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
        backdropFilter: 'blur(8px)', transition: 'all 0.3s ease',
        transform: 'translateZ(0)'
    }
};

const GlobalStyles = () => (
    <style jsx global>{`
        body {
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
            background-attachment: fixed;
            color: white; margin: 0;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        .fade-in { animation: fadeIn 0.5s ease-in-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .slide-up { animation: slideUp 0.3s ease-out; }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

        input[type="range"] {
            flex-grow: 1; margin-left: 12px; height: 6px; -webkit-appearance: none;
            appearance: none; background: #4b5563; border-radius: 3px; outline: none;
            background: linear-gradient(to right, #3b82f6 0%, #3b82f6 var(--progress, 50%), #4b5563 var(--progress, 50%), #4b5563 100%);
        }
        input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none; appearance: none; width: 18px; height: 18px;
            background: #3b82f6; border-radius: 50%; cursor: pointer;
            box-shadow: 0 2px 6px rgba(59, 130, 246, 0.4);
        }
        input[type="range"]::-moz-range-thumb {
            width: 18px; height: 18px; background: #3b82f6; border-radius: 50%;
            cursor: pointer; border: none; box-shadow: 0 2px 6px rgba(59, 130, 246, 0.4);
        }

        .track-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.2); }
        .button-hover:hover { background-color: #2563eb !important; transform: translateY(-1px); }
    `}</style>
);

const TrackVisualizer = () => {
    // --- СОСТОЯНИЯ ---
    const [language, setLanguage] = useState('ru');
    const t = useCallback((key) => translations[language][key] || key, [language]);
    const [tracksLoaded, setTracksLoaded] = useState(false);
    const [tracks, setTracks] = useState([]);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [overallStartTime, setOverallStartTime] = useState(0);
    const [maxTime, setMaxTime] = useState(0);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [showSettings, setShowSettings] = useState(false);
    const [trackOffsetX, setTrackOffsetX] = useState(40);
    const [trackOffsetY, setTrackOffsetY] = useState(0);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [showMetrics, setShowMetrics] = useState({ speed: false, elevation: false, heartRate: false, cadence: false });
    const [trackVisualization, setTrackVisualization] = useState('none');
    const [maxTrackLineWidth, setMaxTrackLineWidth] = useState(10);
    const [editingTrackIndex, setEditingTrackIndex] = useState(null);
    const [showAvatarModal, setShowAvatarModal] = useState(false);
    const [avatarModalMode, setAvatarModalMode] = useState('emoji');
    const [showStats, setShowStats] = useState(true);

    // --- REFS ---
    const imageAvatarCache = useRef({});
    const fileInputRef = useRef(null); // Used for avatar image upload and main file upload
    const mainFileInputRef = useRef(null); // Specifically for main GPX/FIT upload to reset it
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const lastTimeRef = useRef(null);
    const trackColors = ['#ef4444', '#22d3ee', '#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#06b6d4', '#84cc16'];

    // --- ФУНКЦИИ ФОРМАТИРОВАНИЯ ВРЕМЕНИ ---
    const formatTime = useCallback((ms) => {
        if (isNaN(ms) || ms < 0) ms = 0;
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }, []);

    const formatTime24 = useCallback((timestampMs) => {
        if (!timestampMs || timestampMs <= 0 || isNaN(timestampMs)) return "00:00:00";
        const date = new Date(timestampMs);
        return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
    }, []);

    // --- МЕМОИЗИРОВАННЫЕ ВЫЧИСЛЕНИЯ СТАТИСТИКИ ---
    const trackStatistics = useMemo(() => {
        return tracks.map(track => {
            const points = track.points;
            if (points.length < 2) return null;

            const totalDistance = points.reduce((acc, point, i) => {
                if (i === 0) return 0;
                const prev = points[i-1];
                const R = 6371000;
                const phi1 = prev.lat * Math.PI / 180;
                const phi2 = point.lat * Math.PI / 180;
                const deltaPhi = (point.lat - prev.lat) * Math.PI / 180;
                const deltaLambda = (point.lon - prev.lon) * Math.PI / 180;
                const a = Math.sin(deltaPhi/2) * Math.sin(deltaPhi/2) +
                         Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda/2) * Math.sin(deltaLambda/2);
                const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
                return acc + R * c;
            }, 0);

            const speeds = points.map(p => p.speed).filter(s => typeof s === 'number' && s > 0);
            const avgSpeed = speeds.length > 0 ? speeds.reduce((a, b) => a + b, 0) / speeds.length : 0;
            const maxSpeed = speeds.length > 0 ? Math.max(...speeds) : 0;

            const elevations = points.map(p => p.elevation).filter(e => e != null);
            const elevationGain = elevations.length > 1 ?
                elevations.reduce((gain, curr, i) => {
                    if (i === 0) return 0;
                    const diff = curr - elevations[i-1];
                    return gain + (diff > 0 ? diff : 0);
                }, 0) : 0;

            const duration = track.duration;

            return {
                totalDistance: (totalDistance / 1000).toFixed(2),
                avgSpeed: avgSpeed.toFixed(1),
                maxSpeed: maxSpeed.toFixed(1),
                elevationGain: elevationGain.toFixed(0),
                duration: formatTime(duration)
            };
        });
    }, [tracks, formatTime]);

    // --- ПАРСИНГ GPX ---
    const parseGPX = (xmlContent) => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
        const trackPoints = [];
        const trkpts = xmlDoc.getElementsByTagName('trkpt');

        for (let i = 0; i < trkpts.length; i++) {
            const trkpt = trkpts[i];
            const lat = parseFloat(trkpt.getAttribute('lat'));
            const lon = parseFloat(trkpt.getAttribute('lon'));
            const timeElement = trkpt.getElementsByTagName('time')[0];
            const eleElement = trkpt.getElementsByTagName('ele')[0];

            let cadenceVal = null, hrVal = null;
            const extensions = trkpt.getElementsByTagName('extensions')[0];
            if (extensions) {
                const trackPointExtV1 = extensions.getElementsByTagNameNS('http://www.garmin.com/xmlschemas/TrackPointExtension/v1', 'TrackPointExtension')[0];
                const trackPointExtV2 = extensions.getElementsByTagNameNS('http://www.garmin.com/xmlschemas/TrackPointExtension/v2', 'TrackPointExtension')[0];
                const trackPointExt = trackPointExtV1 || trackPointExtV2;

                if (trackPointExt) {
                    const hrNode = trackPointExt.getElementsByTagNameNS(trackPointExt.namespaceURI, 'hr')[0];
                    if (hrNode) hrVal = parseInt(hrNode.textContent);
                    const cadNode = trackPointExt.getElementsByTagNameNS(trackPointExt.namespaceURI, 'cad')[0];
                    if (cadNode) cadenceVal = parseInt(cadNode.textContent);
                }
            }

            if (timeElement) {
                const timestamp = new Date(timeElement.textContent).getTime();
                trackPoints.push({
                    lat, lon, timestamp,
                    elevation: eleElement ? parseFloat(eleElement.textContent) : 0,
                    heartRate: hrVal, cadence: cadenceVal
                });
            }
        }

        trackPoints.sort((a,b) => a.timestamp - b.timestamp);


        // Вычисление скорости
        for (let i = 0; i < trackPoints.length; i++) {
            if (i === 0) {
                 trackPoints[i].speed = 0; // Initial speed for the first point
            } else {
                const prev = trackPoints[i-1];
                const curr = trackPoints[i];
                const timeDiff = (curr.timestamp - prev.timestamp) / 1000; // seconds
                if (timeDiff > 0) {
                    const R = 6371000; // Radius of Earth in meters
                    const phi1 = prev.lat * Math.PI / 180;
                    const phi2 = curr.lat * Math.PI / 180;
                    const deltaPhi = (curr.lat - prev.lat) * Math.PI / 180;
                    const deltaLambda = (curr.lon - prev.lon) * Math.PI / 180;
                    const a = Math.sin(deltaPhi/2) * Math.sin(deltaPhi/2) +
                             Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda/2) * Math.sin(deltaLambda/2);
                    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
                    const distance = R * c; // meters
                    curr.speed = (distance / timeDiff) * 3.6; // km/h
                } else {
                    curr.speed = prev.speed || 0; // Use previous speed or 0 if time diff is zero/negative
                }
            }
        }

        if (trackPoints.length > 0 && trackPoints[0].speed === 0 && trackPoints.length > 1 && trackPoints[1].speed !== undefined) {
            trackPoints[0].speed = trackPoints[1].speed;
        }


        return trackPoints;
    };

    // --- ПАРСИНГ FIT ---
    const parseFIT = (fitFileContent) => {
        return new Promise((resolve, reject) => {
            const fitParser = new FitParser({
                force: true,
                speedUnit: 'km/h', // Important: converts speed to km/h
                lengthUnit: 'm',
                temperatureUnit: 'celsius',
                elapsedRecordField: true,
                mode: 'list',
            });

            fitParser.parse(fitFileContent, (error, data) => {
                if (error) {
                    console.error("Error parsing FIT file:", error);
                    reject(error);
                    return;
                }

                const parsedTrackPoints = [];
                let records = [];

                if (data.records && data.records.length > 0) {
                    records = data.records;
                } else if (data.activity && data.activity.sessions && data.activity.sessions.length > 0) {
                    records = data.activity.sessions.flatMap(session =>
                        session.laps ? session.laps.flatMap(lap => lap.records || []) : []
                    );
                }

                if (!records || records.length === 0) {
                    console.warn("No record messages found in FIT file");
                    resolve([]);
                    return;
                }

                records.forEach(record => {
                    if (record.timestamp && typeof record.position_lat === 'number' && typeof record.position_long === 'number') {
                        const jsTimestamp = record.timestamp.getTime(); // record.timestamp is a JS Date object

                        let speedKmh;
                        // Prefer enhanced_speed if available, then speed. FitParser converts to km/h.
                        if (typeof record.enhanced_speed === 'number') {
                            speedKmh = record.enhanced_speed;
                        } else if (typeof record.speed === 'number') {
                            speedKmh = record.speed;
                        } else {
                            speedKmh = undefined; // Will be calculated later if undefined
                        }

                        parsedTrackPoints.push({
                            lat: record.position_lat,
                            lon: record.position_long,
                            timestamp: jsTimestamp,
                            elevation: typeof record.altitude === 'number' ? record.altitude : 0,
                            heartRate: typeof record.heart_rate === 'number' ? record.heart_rate : null,
                            cadence: typeof record.cadence === 'number' ? record.cadence : null,
                            speed: speedKmh,
                        });
                    }
                });

                // Sort by timestamp
                parsedTrackPoints.sort((a, b) => a.timestamp - b.timestamp);

                // Calculate speed if it's undefined (not provided or not converted by FitParser)
                for (let i = 0; i < parsedTrackPoints.length; i++) {
                    if (i === 0) {
                        if (parsedTrackPoints[i].speed === undefined) {
                            parsedTrackPoints[i].speed = 0; // Initial speed
                        }
                    } else {
                        const prev = parsedTrackPoints[i-1];
                        const curr = parsedTrackPoints[i];
                        if (curr.speed === undefined) {
                            const timeDiff = (curr.timestamp - prev.timestamp) / 1000; // seconds
                            if (timeDiff > 0) {
                                const R = 6371000; // Earth radius in meters
                                const phi1 = prev.lat * Math.PI / 180;
                                const phi2 = curr.lat * Math.PI / 180;
                                const deltaPhi = (curr.lat - prev.lat) * Math.PI / 180;
                                const deltaLambda = (curr.lon - prev.lon) * Math.PI / 180;
                                const a = Math.sin(deltaPhi/2) * Math.sin(deltaPhi/2) +
                                         Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda/2) * Math.sin(deltaLambda/2);
                                const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
                                const distance = R * c; // meters
                                curr.speed = (distance / timeDiff) * 3.6; // km/h
                            } else {
                                curr.speed = prev.speed || 0;
                            }
                        }
                    }
                }

                // If first point's speed is still 0, try to set it from the second point's speed
                if (parsedTrackPoints.length > 0 && parsedTrackPoints[0].speed === 0 && parsedTrackPoints.length > 1 && parsedTrackPoints[1].speed !== undefined) {
                    parsedTrackPoints[0].speed = parsedTrackPoints[1].speed;
                } else if (parsedTrackPoints.length === 1 && parsedTrackPoints[0].speed === undefined) {
                     parsedTrackPoints[0].speed = 0;
                }


                resolve(parsedTrackPoints);
            });
        });
    };


    // --- МЕМОИЗИРОВАННЫЕ ФУНКЦИИ ---
    const getNumEnabledMetrics = useCallback(() =>
        Object.values(showMetrics).filter(Boolean).length, [showMetrics]);

    const coordsToPixels = useMemo(() => {
        if (!tracks || tracks.length === 0) return { convertedTracks: [], bounds: null, metricsHeight: 0 };

        let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
        tracks.forEach(track => track.points.forEach(point => {
            minLat = Math.min(minLat, point.lat);
            maxLat = Math.max(maxLat, point.lat);
            minLon = Math.min(minLon, point.lon);
            maxLon = Math.max(maxLon, point.lon);
        }));

        const canvasWidth = 1200, canvasHeight = 800;
        const latRange = maxLat - minLat;
        const lonRange = maxLon - minLon;
        const margin = 50;
        const numMetrics = getNumEnabledMetrics();
        const metricsGraphHeight = 60;
        const metricsSpacing = 20;
        const metricsTotalHeight = numMetrics > 0 ? (numMetrics * (metricsGraphHeight + metricsSpacing)) - metricsSpacing + 20 : 0;
        const availableHeight = canvasHeight - (2 * margin) - metricsTotalHeight;

        const baseScaleX = (canvasWidth - 2 * margin) / (lonRange || 1);
        const baseScaleY = availableHeight / (latRange || 1);
        const baseScale = Math.min(baseScaleX, baseScaleY);
        const scale = baseScale * zoomLevel;

        const viewCenterX = canvasWidth / 2;
        const viewCenterY = margin + metricsTotalHeight + (availableHeight / 2);
        const mapCenterLat = (minLat + maxLat) / 2;
        const mapCenterLon = (minLon + maxLon) / 2;

        return {
            convertedTracks: tracks.map((track, index) => {
                let currentTrackOffsetX = 0, currentTrackOffsetY = 0;
                if (tracks.length > 1) {
                    currentTrackOffsetX = index * trackOffsetX;
                    currentTrackOffsetY = index * trackOffsetY;
                }
                return {
                    ...track,
                    points: track.points.map(point => ({
                        ...point,
                        x: viewCenterX + (point.lon - mapCenterLon) * scale + currentTrackOffsetX,
                        y: viewCenterY - (point.lat - mapCenterLat) * scale + currentTrackOffsetY
                    }))
                };
            }),
            bounds: { minLat, maxLat, minLon, maxLon },
            scale,
            metricsHeight: metricsTotalHeight
        };
    }, [tracks, zoomLevel, trackOffsetX, trackOffsetY, getNumEnabledMetrics]);

    const getMetricValue = useCallback((point, metric) => ({
        speed: point.speed,
        elevation: point.elevation,
        heartRate: point.heartRate,
        cadence: point.cadence
    })[metric], []);

    const getLineStyle = useCallback((point, track, metric) => {
        if (metric === 'none') return { color: track.color, width: 3 };

        const value = getMetricValue(point, metric);
        if (typeof value !== 'number') return { color: track.color, width: 3 };

        const allValues = track.points.map(p => getMetricValue(p, metric))
            .filter(v => typeof v === 'number' && (metric === 'elevation' || v > 0));

        if (allValues.length === 0) return { color: track.color, width: 3 };

        const minValue = Math.min(...allValues);
        const maxValue = Math.max(...allValues);
        let normalizedValue = 0.5;

        if (maxValue > minValue) {
            normalizedValue = (value - minValue) / (maxValue - minValue);
        } else if (allValues.length > 0 && value === minValue) {
            normalizedValue = 0.5; // If all values are the same, use a mid-point normalization
        }

        normalizedValue = Math.max(0, Math.min(1, normalizedValue)); // Clamp between 0 and 1
        const width = 1 + normalizedValue * (maxTrackLineWidth - 1);
        const alpha = 0.3 + normalizedValue * 0.7;
        const colorVal = track.color + Math.floor(alpha * 255).toString(16).padStart(2, '0');

        return { color: colorVal, width };
    }, [getMetricValue, maxTrackLineWidth]);

    // --- ФУНКЦИИ РЕНДЕРИНГА ---
    const drawMetricsGraph = useCallback((ctx, track, metricName, graphArea, currentAbsTime, overallStartTimeParam, overallMaxTimeParam) => {
        const relevantPoints = track.points.filter(p => typeof getMetricValue(p, metricName) === 'number');
        const allTrackMetricValues = relevantPoints.map(p => getMetricValue(p, metricName));
        const valuesForMinMax = allTrackMetricValues.filter(v => metricName === 'elevation' || v > 0);

        if (relevantPoints.length === 0) return;

        const minValue = valuesForMinMax.length > 0 ? Math.min(...valuesForMinMax) : 0;
        const maxValue = valuesForMinMax.length > 0 ? Math.max(...valuesForMinMax) : (metricName === 'elevation' ? 0 : 1);

        const gradient = ctx.createLinearGradient(0, graphArea.y, 0, graphArea.y + graphArea.height);
        gradient.addColorStop(0, track.color + '40');
        gradient.addColorStop(1, track.color + '10');

        ctx.fillStyle = gradient;
        ctx.strokeStyle = track.color;
        ctx.lineWidth = 2;
        ctx.beginPath();

        let firstPoint = true;
        const pathPoints = [];

        relevantPoints.forEach(point => {
            if (point.timestamp < track.startTime || point.timestamp > track.endTime || point.timestamp > currentAbsTime) return;

            if (overallMaxTimeParam > 0) {
                const timeProgress = (point.timestamp - overallStartTimeParam) / overallMaxTimeParam;
                const x = graphArea.x + timeProgress * graphArea.width;
                const value = getMetricValue(point, metricName);
                let normalizedValue = 0;

                if (maxValue > minValue) {
                    normalizedValue = (value - minValue) / (maxValue - minValue);
                } else if (value === minValue && valuesForMinMax.length > 0) {
                    normalizedValue = 0.5;
                }

                normalizedValue = Math.max(0, Math.min(1, normalizedValue));
                const plotY = graphArea.y + graphArea.height - (normalizedValue * (graphArea.height - 10)) - 5;

                if (x >= graphArea.x && x <= graphArea.x + graphArea.width) {
                    pathPoints.push({x, y: plotY});
                    if (firstPoint) {
                        ctx.moveTo(x, plotY);
                        firstPoint = false;
                    } else {
                        ctx.lineTo(x, plotY);
                    }
                }
            }
        });

        if (pathPoints.length > 0) {
            ctx.lineTo(pathPoints[pathPoints.length - 1].x, graphArea.y + graphArea.height);
            ctx.lineTo(pathPoints[0].x, graphArea.y + graphArea.height);
            ctx.closePath();
            ctx.fill();

            ctx.beginPath();
            pathPoints.forEach((point, i) => {
                if (i === 0) ctx.moveTo(point.x, point.y);
                else ctx.lineTo(point.x, point.y);
            });
            ctx.stroke();
        }

        if (overallMaxTimeParam > 0 && currentAbsTime >= overallStartTimeParam && currentAbsTime <= overallStartTimeParam + overallMaxTimeParam) {
            const currentGlobalTimeProgress = (currentAbsTime - overallStartTimeParam) / overallMaxTimeParam;
            const currentX = graphArea.x + currentGlobalTimeProgress * graphArea.width;
            const trackStartProgressOnGlobal = (track.startTime - overallStartTimeParam) / overallMaxTimeParam;
            const trackEndProgressOnGlobal = (track.endTime - overallStartTimeParam) / overallMaxTimeParam;
            const trackStartXOnGraph = graphArea.x + trackStartProgressOnGlobal * graphArea.width;
            const trackEndXOnGraph = graphArea.x + trackEndProgressOnGlobal * graphArea.width;

            if (currentX >= trackStartXOnGraph && currentX <= trackEndXOnGraph &&
                currentX >= graphArea.x && currentX <= graphArea.x + graphArea.width) {
                ctx.strokeStyle = '#fff';
                ctx.lineWidth = 2;
                ctx.setLineDash([5, 5]);
                ctx.beginPath();
                ctx.moveTo(currentX, graphArea.y);
                ctx.lineTo(currentX, graphArea.y + graphArea.height);
                ctx.stroke();
                ctx.setLineDash([]);
            }
        }

        ctx.fillStyle = '#aaa';
        ctx.font = '10px monospace';
        ctx.fillText(maxValue.toFixed(0), graphArea.x + 5, graphArea.y + 15);
    }, [getMetricValue]);

    const drawAllMetrics = useCallback((ctx, convertedTracks, canvasWidth, metricsTotalHeight, currentAbsTime, overallStartTimeParam, overallMaxTimeParam) => {
        if (metricsTotalHeight === 0) return;

        const enabledMetrics = Object.entries(showMetrics).filter(([_, show]) => show);
        const metricGraphHeight = 60;
        const metricSpacing = 20;
        let currentY = 20;

        enabledMetrics.forEach(([metricName, _]) => {
            const graphYForDrawing = currentY + 15;
            const graphArea = { x: 20, y: graphYForDrawing, width: canvasWidth - 40, height: metricGraphHeight };

            const bgGradient = ctx.createLinearGradient(0, graphArea.y, 0, graphArea.y + graphArea.height);
            bgGradient.addColorStop(0, '#374151');
            bgGradient.addColorStop(1, '#1f2937');
            ctx.fillStyle = bgGradient;
            ctx.fillRect(graphArea.x, graphArea.y, graphArea.width, graphArea.height);

            ctx.strokeStyle = '#4b5563';
            ctx.lineWidth = 1;
            ctx.strokeRect(graphArea.x, graphArea.y, graphArea.width, graphArea.height);

            ctx.fillStyle = '#e5e7eb';
            ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

            let labelKey = '';
            if (metricName === 'speed') labelKey = 'metricSpeedLabel';
            else if (metricName === 'elevation') labelKey = 'metricElevationLabel';
            else if (metricName === 'heartRate') labelKey = 'metricHeartRateLabel';
            else if (metricName === 'cadence') labelKey = 'metricCadenceLabel';

            ctx.fillText(t(labelKey) || metricName.toUpperCase(), graphArea.x + 8, currentY + 10);

            convertedTracks.forEach(track => {
                drawMetricsGraph(ctx, track, metricName, graphArea, currentAbsTime, overallStartTimeParam, overallMaxTimeParam);
            });

            currentY += metricGraphHeight + metricSpacing;
        });
    }, [showMetrics, drawMetricsGraph, t]);

    const drawTracksOnMap = useCallback((ctx, convertedTracks, currentAbsTime) => {
        convertedTracks.forEach((track) => {
            const pointsToDraw = track.points.filter(p =>
                p.timestamp >= track.startTime &&
                p.timestamp <= currentAbsTime &&
                p.x >= -50 && p.x <= 1250 &&
                p.y >= -50 && p.y <= 850
            );

            if (pointsToDraw.length === 0) return;

            if (pointsToDraw.length > 1) {
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';

                let lastStyle = null;
                ctx.beginPath();

                for (let i = 1; i < pointsToDraw.length; i++) {
                    const p1 = pointsToDraw[i-1];
                    const p2 = pointsToDraw[i];
                    const currentStyle = getLineStyle(p2, track, trackVisualization);

                    if (!lastStyle) {
                        lastStyle = currentStyle;
                        ctx.strokeStyle = currentStyle.color;
                        ctx.lineWidth = currentStyle.width;
                        ctx.moveTo(p1.x, p1.y);
                    } else if (currentStyle.color !== lastStyle.color || Math.abs(currentStyle.width - lastStyle.width) > 0.5) {
                        ctx.lineTo(p1.x, p1.y);
                        ctx.stroke();
                        ctx.beginPath();
                        ctx.strokeStyle = currentStyle.color;
                        ctx.lineWidth = currentStyle.width;
                        ctx.moveTo(p1.x, p1.y);
                        lastStyle = currentStyle;
                    }
                    ctx.lineTo(p2.x, p2.y);
                }
                ctx.stroke();
            }

            const lastPoint = pointsToDraw[pointsToDraw.length - 1];
            const avatarSize = 36;
            const avatarRadius = avatarSize / 2;
            const cacheKey = `${track.id}_${track.avatarImageSrc ? track.avatarImageSrc.slice(-20) : (track.avatarEmoji || 'default')}`;

            const avatarX = lastPoint.x;
            const avatarY = lastPoint.y;

            ctx.save();

            ctx.shadowColor = 'rgba(0,0,0,0.3)';
            ctx.shadowBlur = 8;
            ctx.shadowOffsetX = 2;
            ctx.shadowOffsetY = 2;

            if (track.avatarImageSrc && imageAvatarCache.current[cacheKey] && imageAvatarCache.current[cacheKey].complete) {
                const img = imageAvatarCache.current[cacheKey];
                ctx.beginPath();
                ctx.arc(avatarX, avatarY, avatarRadius, 0, Math.PI * 2, true);
                ctx.closePath();
                ctx.clip();
                ctx.drawImage(img, avatarX - avatarRadius, avatarY - avatarRadius, avatarSize, avatarSize);
            } else if (track.avatarEmoji) {
                ctx.font = `${avatarSize * 0.8}px sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(track.avatarEmoji, avatarX, avatarY);
            } else {
                const gradient = ctx.createRadialGradient(avatarX, avatarY, 0, avatarX, avatarY, avatarRadius);
                gradient.addColorStop(0, track.color);
                gradient.addColorStop(1, track.color + '80');

                ctx.beginPath();
                ctx.arc(avatarX, avatarY, avatarRadius, 0, Math.PI * 2, true);
                ctx.closePath();
                ctx.fillStyle = gradient;
                ctx.fill();

                ctx.strokeStyle = 'rgba(255,255,255,0.8)';
                ctx.lineWidth = 2;
                ctx.stroke();

                ctx.font = `bold ${avatarRadius * 0.8}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
                ctx.fillStyle = 'white';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(track.name ? track.name.substring(0, 1).toUpperCase() : '?', avatarX, avatarY);
            }

            ctx.restore();
            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';
        });
    }, [getLineStyle, trackVisualization]);

    const drawMapGrid = useCallback((ctx, mapStartY, canvasWidth, canvasHeight) => {
        ctx.strokeStyle = 'rgba(55, 65, 81, 0.3)';
        ctx.lineWidth = 1;
        ctx.setLineDash([2, 4]);

        for (let i = 0; i < canvasWidth; i += 100) {
            ctx.beginPath();
            ctx.moveTo(i, mapStartY);
            ctx.lineTo(i, canvasHeight);
            ctx.stroke();
        }

        for (let i = mapStartY; i < canvasHeight; i += 100) {
            ctx.beginPath();
            ctx.moveTo(0, i);
            ctx.lineTo(canvasWidth, i);
            ctx.stroke();
        }

        ctx.setLineDash([]);
    }, []);

    const drawCurrentTimeDisplay = useCallback((ctx, currentAbsTime, canvasWidth) => {
        const padding = 15;
        const bgWidth = 140;
        const bgHeight = 40;
        const x = canvasWidth - bgWidth - padding;
        const y = padding;

        const gradient = ctx.createLinearGradient(x, y, x, y + bgHeight);
        gradient.addColorStop(0, 'rgba(0,0,0,0.8)');
        gradient.addColorStop(1, 'rgba(0,0,0,0.6)');
        ctx.fillStyle = gradient;
        ctx.fillRect(x, y, bgWidth, bgHeight);

        ctx.strokeStyle = 'rgba(255,255,255,0.2)';
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, bgWidth, bgHeight);

        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 18px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace';
        ctx.textAlign = 'center';
        ctx.fillText(formatTime24(currentAbsTime), x + bgWidth/2, y + bgHeight/2 + 6);
        ctx.textAlign = 'left';
    }, [formatTime24]);

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');

        const bgGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
        bgGradient.addColorStop(0, '#0f172a');
        bgGradient.addColorStop(0.5, '#1e293b');
        bgGradient.addColorStop(1, '#0f172a');
        ctx.fillStyle = bgGradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        if (tracks.length === 0 && tracksLoaded) return;
        if (!tracksLoaded || tracks.length === 0) return;

        const { convertedTracks, metricsHeight: metricsTotalHeightCalculated } = coordsToPixels;
        const currentAbsTime = overallStartTime + currentTime;

        drawAllMetrics(ctx, convertedTracks, canvas.width, metricsTotalHeightCalculated,
                      currentAbsTime, overallStartTime, maxTime);

        const mapStartY = metricsTotalHeightCalculated > 0 ? metricsTotalHeightCalculated + 20 : 50;
        drawMapGrid(ctx, mapStartY, canvas.width, canvas.height);
        drawTracksOnMap(ctx, convertedTracks, currentAbsTime);
        drawCurrentTimeDisplay(ctx, currentAbsTime, canvas.width);
    }, [tracks, currentTime, overallStartTime, maxTime, coordsToPixels, drawAllMetrics,
        drawTracksOnMap, drawMapGrid, drawCurrentTimeDisplay, tracksLoaded]);

    useEffect(() => {
        if (tracksLoaded) {
            draw();
        } else {
            const canvas = canvasRef.current;
            if (canvas) {
                const ctx = canvas.getContext('2d');
                const bgGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
                bgGradient.addColorStop(0, '#0f172a');
                bgGradient.addColorStop(0.5, '#1e293b');
                bgGradient.addColorStop(1, '#0f172a');
                ctx.fillStyle = bgGradient;
                ctx.fillRect(0, 0, canvas.width, canvas.height);
            }
        }
    }, [draw, tracksLoaded, currentTime]); // currentTime ensures redraw on manual timeline scrub

    const animateLoop = useCallback((timestamp) => {
        if (!animationRef.current) return;

        if (!lastTimeRef.current) {
            lastTimeRef.current = timestamp;
            if(animationRef.current) animationRef.current = requestAnimationFrame(animateLoop);
            return;
        }

        const deltaTime = timestamp - lastTimeRef.current;
        const timeToAdvance = deltaTime * playbackSpeed * 10; // Multiplier for perceived speed
        lastTimeRef.current = timestamp;

        setCurrentTime(prevCurrentTime => {
            const newTime = prevCurrentTime + timeToAdvance;
            if (newTime >= maxTime) {
                setIsPlaying(false);
                return maxTime;
            }
            return newTime;
        });

        if (animationRef.current) { // Check again as state change might have cancelled it
            animationRef.current = requestAnimationFrame(animateLoop);
        }
    }, [playbackSpeed, maxTime, setIsPlaying, setCurrentTime]); // Removed maxTime from here as it doesn't change during animation loop

    useEffect(() => {
        if (isPlaying && maxTime > 0 && tracksLoaded) {
            lastTimeRef.current = null; // Reset lastTimeRef when starting/resuming
            animationRef.current = requestAnimationFrame(animateLoop);
        } else {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }
        }
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }
        };
    }, [isPlaying, animateLoop, tracksLoaded, maxTime]); // Added maxTime here as animation depends on it

    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        const newParsedTracks = [];
        let currentTrackCount = tracks.length;

        for (const file of files) {
            let points = [];
            try {
                const fileNameLower = file.name.toLowerCase();
                if (fileNameLower.endsWith('.gpx')) {
                    const content = await file.text();
                    points = parseGPX(content);
                } else if (fileNameLower.endsWith('.fit')) {
                    const arrayBufferContent = await file.arrayBuffer();
                    points = await parseFIT(arrayBufferContent); // parseFIT returns a Promise
                } else {
                    console.warn(`Unsupported file type: ${file.name}. Only .gpx and .fit are supported.`);
                    alert(`Unsupported file type: ${file.name}. Only .gpx and .fit are supported.`);
                    continue;
                }

                if (points && points.length > 0) {
                    const trackName = file.name.replace(/\.[^/.]+$/, '');
                    newParsedTracks.push({
                        id: `${Date.now()}_${currentTrackCount + newParsedTracks.length}`,
                        name: trackName,
                        points,
                        startTime: points[0].timestamp,
                        endTime: points[points.length - 1].timestamp,
                        duration: points[points.length - 1].timestamp - points[0].timestamp,
                        color: trackColors[(currentTrackCount + newParsedTracks.length) % trackColors.length],
                        avatarEmoji: null,
                        avatarImageSrc: null
                    });
                } else {
                    console.warn(`No track points found or parsed for file: ${file.name}`);
                    if (points === null) { // Indicates a parsing error caught within parseFIT
                         alert(`Could not parse file: ${file.name}. Check console for details.`);
                    }
                }
            } catch (error) {
                console.error('Error processing file:', file.name, error);
                alert(`Error processing file ${file.name}: ${error.message}`);
            }
        }

        if (newParsedTracks.length > 0) {
            const allTracks = [...tracks, ...newParsedTracks];
            setTracks(allTracks);

            const firstStartTime = Math.min(...allTracks.map(t => t.startTime));
            const lastEndTime = Math.max(...allTracks.map(t => t.endTime));
            setOverallStartTime(firstStartTime);
            setMaxTime(lastEndTime - firstStartTime);
            setCurrentTime(0);
            setIsPlaying(false); // Stop playback when new tracks are loaded
            setTracksLoaded(true);
        }

        // Reset file input to allow re-uploading the same file
        if (event.target) {
            event.target.value = '';
        }
        if (mainFileInputRef.current) { // Also reset the specific ref if used
             mainFileInputRef.current.value = '';
        }
    };

    const exportFrame = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || !tracksLoaded || tracks.length === 0) return;

        const link = document.createElement('a');
        link.download = `gps-track-${formatTime24(overallStartTime + currentTime)}.png`;
        link.href = canvas.toDataURL('image/png', 1.0);
        link.click();
    }, [currentTime, overallStartTime, formatTime24, tracksLoaded, tracks]);


    const togglePlayback = () => setIsPlaying(!isPlaying);
    const resetAnimation = () => {
        setCurrentTime(0);
        setIsPlaying(false);
        setZoomLevel(1);
        setTrackOffsetX(40);
        setTrackOffsetY(0);
        // Redraw after reset
        if (tracksLoaded) {
             // Calling draw directly can be problematic if state updates are not immediate.
             // useEffect on currentTime will trigger a redraw.
        }
    };
    const clearTracksData = () => {
        setTracks([]);
        setCurrentTime(0);
        setMaxTime(0);
        setOverallStartTime(0);
        setIsPlaying(false);
        setZoomLevel(1);
        setTrackOffsetX(40);
        setTrackOffsetY(0);
        setTracksLoaded(false);
        setShowSettings(false);
        imageAvatarCache.current = {};
        if (mainFileInputRef.current) mainFileInputRef.current.value = ''; // Clear file input
    };

    const handleTimelineUiClick = (event) => {
        if (maxTime === 0) return;
        const timelineRect = event.currentTarget.getBoundingClientRect();
        const clickX = event.clientX - timelineRect.left;
        const progress = Math.max(0, Math.min(1, clickX / timelineRect.width));
        const newCurrentTime = progress * maxTime;
        setCurrentTime(newCurrentTime);
    };

    const adjustTrackOffsetX = (amount) => setTrackOffsetX(prev => prev + amount);
    const adjustTrackOffsetY = (amount) => setTrackOffsetY(prev => prev + amount);
    const adjustZoom = (amount) => setZoomLevel(prev => Math.max(0.1, parseFloat((prev + amount).toFixed(1))));
    const toggleMetricDisplay = (metric) => {
        setShowMetrics(prev => ({ ...prev, [metric]: !prev[metric] }));
    };
    const toggleLanguage = () => {
        setLanguage(prevLang => prevLang === 'ru' ? 'en' : 'ru');
    };

    const handleEditTrackName = (trackIndex) => {
        const newName = prompt(t('enterNewName'), tracks[trackIndex].name);
        if (newName !== null && newName.trim() !== "") {
            setTracks(prevTracks => prevTracks.map((track, idx) =>
                idx === trackIndex ? { ...track, name: newName.trim() } : track
            ));
        }
    };

    const openAvatarModal = (index, mode) => {
        setEditingTrackIndex(index);
        setAvatarModalMode(mode);
        setShowAvatarModal(true);
    };

    const handleSelectEmoji = (emoji) => {
        if (editingTrackIndex !== null) {
            const trackToUpdate = tracks[editingTrackIndex];
            const oldCacheKey = `${trackToUpdate.id}_${trackToUpdate.avatarImageSrc ? trackToUpdate.avatarImageSrc.slice(-20) : (trackToUpdate.avatarEmoji || 'default')}`;
            delete imageAvatarCache.current[oldCacheKey];

            setTracks(prevTracks => prevTracks.map((track, idx) =>
                idx === editingTrackIndex ? { ...track, avatarEmoji: emoji, avatarImageSrc: null } : track
            ));
        }
        setShowAvatarModal(false);
    };

    const handleImageFileSelected = (event) => {
        const file = event.target.files[0];
        if (file && editingTrackIndex !== null) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const imgSrc = e.target.result;
                const trackToUpdate = tracks[editingTrackIndex];
                const oldCacheKey = `${trackToUpdate.id}_${trackToUpdate.avatarImageSrc ? trackToUpdate.avatarImageSrc.slice(-20) : (trackToUpdate.avatarEmoji || 'default')}`;
                delete imageAvatarCache.current[oldCacheKey];

                setTracks(prevTracks => prevTracks.map((track, idx) =>
                    idx === editingTrackIndex ? { ...track, avatarImageSrc: imgSrc, avatarEmoji: null } : track
                ));

                const img = new Image();
                img.onload = () => {
                    imageAvatarCache.current[`${trackToUpdate.id}_${imgSrc.slice(-20)}`] = img;
                    if (tracksLoaded) draw(); // Force redraw after image load
                };
                img.onerror = () => {
                    console.error("Failed to load image for avatar");
                };
                img.src = imgSrc;
            };
            reader.readAsDataURL(file);
        }
        setShowAvatarModal(false);
        if (fileInputRef.current) fileInputRef.current.value = ""; // Reset avatar file input
    };

    const handleRemoveCustomAvatar = () => {
        if (editingTrackIndex !== null) {
            const trackToUpdate = tracks[editingTrackIndex];
            const oldCacheKey = `${trackToUpdate.id}_${trackToUpdate.avatarImageSrc ? trackToUpdate.avatarImageSrc.slice(-20) : (trackToUpdate.avatarEmoji || 'default')}`;
            delete imageAvatarCache.current[oldCacheKey];

            setTracks(prevTracks => prevTracks.map((track, idx) =>
                idx === editingTrackIndex ? { ...track, avatarImageSrc: null, avatarEmoji: null } : track
            ));
        }
        setShowAvatarModal(false);
    };

    if (!tracksLoaded) {
        return (
            <>
                <GlobalStyles />
                <div style={{
                    ...modernStyles.container,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    textAlign: 'center'
                }} className="fade-in">
                    <div style={{
                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(147, 51, 234, 0.1))',
                        padding: '60px 40px',
                        borderRadius: '24px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        maxWidth: '600px'
                    }}>
                        <h1 style={{
                            fontSize: '3.5em',
                            marginBottom: '20px',
                            background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontWeight: '800'
                        }}>
                            {t('title')}
                        </h1>
                        <p style={{ fontSize: '1.3em', marginBottom: '10px', color: '#d1d5db' }}>
                            {t('initialLoadMessage')}
                        </p>
                        <p style={{ fontSize: '1em', color: '#9ca3af', marginBottom: '40px' }}>
                            {t('initialLoadSubMessage')}
                        </p>
                        <label style={{
                            ...modernStyles.button,
                            padding: '20px 40px',
                            fontSize: '1.3em',
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                            boxShadow: '0 8px 32px rgba(59, 130, 246, 0.4)',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                        }} className="button-hover">
                            <Upload size={28} />
                            {t('loadInitialGpx')}
                            <input
                                type="file"
                                multiple
                                accept=".gpx,.fit"
                                onChange={handleFileUpload}
                                style={{ display: 'none' }}
                                ref={mainFileInputRef}
                            />
                        </label>
                        <div style={{ marginTop: '40px' }}>
                            <button
                                onClick={toggleLanguage}
                                style={{
                                    ...modernStyles.button,
                                    backgroundColor: 'transparent',
                                    border: '1px solid #3b82f6',
                                    borderRadius: '12px'
                                }}
                                className="button-hover"
                            >
                                <Languages size={20} />
                                {language === 'ru' ? t('languageSwitchToEN') : t('languageSwitchToRU')}
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <GlobalStyles />
            <div style={modernStyles.container} className="fade-in">
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                    padding: '0 8px'
                }}>
                    <h1 style={{
                        fontSize: '28px',
                        margin: 0,
                        color: '#f8fafc',
                        fontWeight: '700',
                        background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    }}>
                        {t('title')}
                    </h1>
                    <button
                        onClick={toggleLanguage}
                        style={{
                            ...modernStyles.button,
                            ...modernStyles.buttonSmall,
                            backgroundColor: 'transparent',
                            border: '1px solid #3b82f6'
                        }}
                        className="button-hover"
                    >
                        <Languages size={16} />
                        {language === 'ru' ? t('languageSwitchToEN') : t('languageSwitchToRU')}
                    </button>
                </div>

                {showStats && tracks.length > 0 && trackStatistics.filter(Boolean).length > 0 && (
                    <div style={{
                        ...modernStyles.card,
                        ...modernStyles.cardGradient,
                        marginBottom: '20px'
                    }} className="slide-up">
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '15px'
                        }}>
                            <h3 style={{
                                margin: 0,
                                color: '#e5e7eb',
                                fontSize: '1.1em',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <BarChart3 size={20} />
                                {t('statistics')}
                            </h3>
                            <button
                                onClick={() => setShowStats(false)}
                                style={{
                                    ...modernStyles.button,
                                    ...modernStyles.buttonSmall,
                                    backgroundColor: 'transparent'
                                }}
                            >
                                <Minus size={14} />
                            </button>
                        </div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                            gap: '15px'
                        }}>
                            {tracks.map((track, index) => {
                                const stats = trackStatistics[index];
                                if (!stats) return null;

                                return (
                                    <div key={track.id} style={{
                                        ...modernStyles.statsCard,
                                        borderColor: track.color,
                                        borderWidth: '2px'
                                    }}>
                                        <div style={{
                                            color: track.color,
                                            fontWeight: 'bold',
                                            fontSize: '0.9em',
                                            marginBottom: '8px',
                                            textAlign: 'center'
                                        }}>
                                            {track.name.length > 12 ? track.name.substring(0, 10) + '...' : track.name}
                                        </div>
                                        <div style={{ display: 'grid', gap: '4px', fontSize: '0.8em', width: '100%' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span style={{ color: '#9ca3af' }}>{t('totalDistance')}:</span>
                                                <span style={{ color: '#e5e7eb', fontWeight: '500' }}>{stats.totalDistance} км</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span style={{ color: '#9ca3af' }}>{t('avgSpeed')}:</span>
                                                <span style={{ color: '#e5e7eb', fontWeight: '500' }}>{stats.avgSpeed} {t('speedUnit')}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span style={{ color: '#9ca3af' }}>{t('maxSpeed')}:</span>
                                                <span style={{ color: '#e5e7eb', fontWeight: '500' }}>{stats.maxSpeed} {t('speedUnit')}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span style={{ color: '#9ca3af' }}>{t('elevationGain')}:</span>
                                                <span style={{ color: '#e5e7eb', fontWeight: '500' }}>{stats.elevationGain} {t('elevationUnit')}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span style={{ color: '#9ca3af' }}>{t('duration')}:</span>
                                                <span style={{ color: '#e5e7eb', fontWeight: '500' }}>{stats.duration}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {tracks.length > 0 && (
                    <div style={{
                        ...modernStyles.card,
                        ...modernStyles.cardGradient,
                        padding: '15px',
                        marginBottom: '20px'
                    }} className="slide-up">
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '12px',
                            alignItems: 'center'
                        }}>
                            {tracks.map((track, index) => {
                                const firstLetter = track.name ? track.name.substring(0, 1).toUpperCase() : '?';
                                const avatarDisplay = track.avatarImageSrc && imageAvatarCache.current[`${track.id}_${track.avatarImageSrc.slice(-20)}`]
                                    ? <img
                                        src={track.avatarImageSrc}
                                        alt="avatar"
                                        style={{
                                            width: '28px',
                                            height: '28px',
                                            borderRadius: '50%',
                                            marginRight: '10px',
                                            objectFit: 'cover',
                                            border: `2px solid ${track.color}`
                                        }}
                                      />
                                    : <span style={{
                                        fontSize: '20px',
                                        marginRight: '10px',
                                        width: '28px',
                                        height: '28px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        color: track.avatarEmoji ? 'inherit' : 'white',
                                        backgroundColor: track.avatarEmoji ? 'transparent' : track.color,
                                        borderRadius: '50%',
                                        border: `2px solid ${track.color}`
                                      }}>
                                        {track.avatarEmoji || firstLetter}
                                      </span>;

                                return (
                                    <div
                                        key={track.id}
                                        style={{
                                            ...modernStyles.animatedTrackCard,
                                            borderColor: track.color,
                                            display: 'flex',
                                            alignItems: 'center',
                                            minWidth: '200px',
                                            maxWidth: '250px'
                                        }}
                                        className="track-card"
                                    >
                                        {avatarDisplay}
                                        <div style={{ flexGrow: 1, minWidth: 0 }}>
                                            <span style={{
                                                fontWeight: '600',
                                                color: track.color,
                                                fontSize: '0.95em',
                                                display: 'block',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }} title={track.name}>
                                                {track.name.length > 15 ? track.name.substring(0, 13) + "..." : track.name}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', gap: '4px', marginLeft: '8px' }}>
                                            <button
                                                onClick={() => handleEditTrackName(index)}
                                                title={t('editNameTooltip')}
                                                style={{
                                                    ...modernStyles.button,
                                                    ...modernStyles.buttonSmall,
                                                    padding: '4px 6px',
                                                    backgroundColor: 'rgba(59, 130, 246, 0.2)',
                                                    border: '1px solid rgba(59, 130, 246, 0.3)'
                                                }}
                                                className="button-hover"
                                            >
                                                <Edit3 size={12}/>
                                            </button>
                                            <button
                                                onClick={() => openAvatarModal(index, 'emoji')}
                                                title={t('editAvatarTooltip')}
                                                style={{
                                                    ...modernStyles.button,
                                                    ...modernStyles.buttonSmall,
                                                    padding: '4px 6px',
                                                    backgroundColor: 'rgba(59, 130, 246, 0.2)',
                                                    border: '1px solid rgba(59, 130, 246, 0.3)'
                                                }}
                                                className="button-hover"
                                            >
                                                <Smile size={12}/>
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div style={{
                    ...modernStyles.card,
                    ...modernStyles.cardGradient
                }} className="slide-up">
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '15px'
                    }}>
                        <label style={{
                            ...modernStyles.button,
                            cursor: 'pointer',
                            background: 'linear-gradient(135deg, #059669, #047857)'
                        }} className="button-hover">
                            <Upload size={18} />
                            {t('loadGpx')}
                            <input
                                type="file"
                                multiple
                                accept=".gpx,.fit"
                                onChange={handleFileUpload}
                                style={{ display: 'none' }}
                                ref={mainFileInputRef} // Use the specific ref here
                            />
                        </label>

                        <button
                            onClick={togglePlayback}
                            disabled={tracks.length === 0}
                            style={{
                                ...modernStyles.button,
                                ...(tracks.length === 0 && modernStyles.buttonDisabled),
                                background: isPlaying
                                    ? 'linear-gradient(135deg, #dc2626, #b91c1c)'
                                    : 'linear-gradient(135deg, #059669, #047857)'
                            }}
                            className={tracks.length > 0 ? "button-hover" : ""}
                        >
                            {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                            {t(isPlaying ? 'pause' : 'play')}
                        </button>

                        <button
                            onClick={resetAnimation}
                            disabled={tracks.length === 0}
                            style={{
                                ...modernStyles.button,
                                ...(tracks.length === 0 && modernStyles.buttonDisabled),
                                backgroundColor: '#6366f1'
                            }}
                            className={tracks.length > 0 ? "button-hover" : ""}
                        >
                            <RotateCcw size={18} />
                            {t('reset')}
                        </button>

                        <button
                            onClick={exportFrame}
                            disabled={tracks.length === 0}
                            style={{
                                ...modernStyles.button,
                                ...(tracks.length === 0 && modernStyles.buttonDisabled),
                                backgroundColor: '#7c3aed'
                            }}
                            className={tracks.length > 0 ? "button-hover" : ""}
                        >
                            <Download size={18} />
                            {t('exportFrame')}
                        </button>

                        <button
                            onClick={clearTracksData}
                            style={{
                                ...modernStyles.button,
                                background: 'linear-gradient(135deg, #dc2626, #b91c1c)'
                            }}
                            className="button-hover"
                        >
                            {t('clearAll')}
                        </button>

                        <button
                            onClick={() => setShowSettings(!showSettings)}
                            style={{
                                ...modernStyles.button,
                                marginLeft: 'auto',
                                backgroundColor: showSettings ? '#4338ca' : '#6366f1'
                            }}
                            className="button-hover"
                        >
                            <Settings size={18} />
                            {showSettings ? t('close') : t('settings')}
                        </button>

                        {!showStats && tracks.length > 0 && (
                            <button
                                onClick={() => setShowStats(true)}
                                style={{
                                    ...modernStyles.button,
                                    backgroundColor: '#059669'
                                }}
                                className="button-hover"
                            >
                                <TrendingUp size={18} />
                                {t('statistics')}
                            </button>
                        )}
                    </div>

                    {showSettings && (
                        <div style={{
                            paddingTop: '20px',
                            borderTop: '1px solid #374151',
                            marginTop: '20px'
                        }} className="fade-in">
                            <div style={{
                                marginBottom: '25px',
                                paddingBottom: '15px',
                                borderBottom: '1px dashed #4b5563'
                            }}>
                                <h3 style={{
                                    fontSize: '1.2em',
                                    color: '#d1d5db',
                                    marginBottom: '15px',
                                    fontWeight: '600'
                                }}>{t('generalSettings')}</h3>

                                <div style={{
                                    marginBottom: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    flexWrap: 'wrap'
                                }}>
                                    <span style={{
                                        minWidth: '120px',
                                        textAlign: 'right',
                                        fontSize: '0.95em',
                                        color: '#d1d5db',
                                        fontWeight: '500'
                                    }}>{t('playbackSpeed')}</span>
                                    <span style={{
                                        minWidth: '45px',
                                        textAlign: 'center',
                                        backgroundColor: '#374151',
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        fontSize: '0.9em',
                                        fontWeight: '600'
                                    }}>{playbackSpeed.toFixed(1)}x</span>
                                    <input
                                        type="range"
                                        min="0.1"
                                        max="30"
                                        step="0.1"
                                        value={playbackSpeed}
                                        onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
                                        style={{
                                            '--progress': `${((playbackSpeed - 0.1) / (30 - 0.1)) * 100}%`
                                        }}
                                    />
                                </div>

                                <div style={{
                                    marginBottom: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    flexWrap: 'wrap'
                                }}>
                                    <span style={{
                                        minWidth: '120px',
                                        textAlign: 'right',
                                        fontSize: '0.95em',
                                        color: '#d1d5db',
                                        fontWeight: '500'
                                    }}>{t('zoom')}</span>
                                    <button
                                        onClick={() => adjustZoom(-0.1)}
                                        style={{
                                            ...modernStyles.button,
                                            ...modernStyles.buttonSmall,
                                            backgroundColor: '#4b5563'
                                        }}
                                        className="button-hover"
                                    >
                                        <Minus size={14} />
                                    </button>
                                    <span style={{
                                        minWidth: '45px',
                                        textAlign: 'center',
                                        backgroundColor: '#374151',
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        fontSize: '0.9em',
                                        fontWeight: '600'
                                    }}>{zoomLevel.toFixed(1)}x</span>
                                    <button
                                        onClick={() => adjustZoom(0.1)}
                                        style={{
                                            ...modernStyles.button,
                                            ...modernStyles.buttonSmall,
                                            backgroundColor: '#4b5563'
                                        }}
                                        className="button-hover"
                                    >
                                        <Plus size={14} />
                                    </button>
                                </div>
                            </div>

                            {tracks.length > 1 && (
                                <div style={{
                                    marginBottom: '25px',
                                    paddingBottom: '15px',
                                    borderBottom: '1px dashed #4b5563'
                                }}>
                                    <h3 style={{
                                        fontSize: '1.2em',
                                        color: '#d1d5db',
                                        marginBottom: '15px',
                                        fontWeight: '600'
                                    }}>{t('trackSettings')}</h3>

                                    <div style={{
                                        marginBottom: '15px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        flexWrap: 'wrap'
                                    }}>
                                        <span style={{
                                            minWidth: '120px',
                                            textAlign: 'right',
                                            fontSize: '0.95em',
                                            color: '#d1d5db',
                                            fontWeight: '500'
                                        }}>{t('trackOffsetX')}</span>
                                        <button
                                            onClick={() => adjustTrackOffsetX(-5)}
                                            style={{
                                                ...modernStyles.button,
                                                ...modernStyles.buttonSmall,
                                                backgroundColor: '#4b5563'
                                            }}
                                            className="button-hover"
                                        >
                                            <ArrowLeft size={14} />
                                        </button>
                                        <span style={{
                                            minWidth: '40px',
                                            textAlign: 'center',
                                            backgroundColor: '#374151',
                                            padding: '4px 8px',
                                            borderRadius: '6px',
                                            fontSize: '0.9em',
                                            fontWeight: '600'
                                        }}>{trackOffsetX}</span>
                                        <button
                                            onClick={() => adjustTrackOffsetX(5)}
                                            style={{
                                                ...modernStyles.button,
                                                ...modernStyles.buttonSmall,
                                                backgroundColor: '#4b5563'
                                            }}
                                            className="button-hover"
                                        >
                                            <ArrowRight size={14} />
                                        </button>
                                    </div>

                                    <div style={{
                                        marginBottom: '15px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        flexWrap: 'wrap'
                                    }}>
                                        <span style={{
                                            minWidth: '120px',
                                            textAlign: 'right',
                                            fontSize: '0.95em',
                                            color: '#d1d5db',
                                            fontWeight: '500'
                                        }}>{t('trackOffsetY')}</span>
                                        <button
                                            onClick={() => adjustTrackOffsetY(-5)}
                                            style={{
                                                ...modernStyles.button,
                                                ...modernStyles.buttonSmall,
                                                backgroundColor: '#4b5563'
                                            }}
                                            className="button-hover"
                                        >
                                            <ArrowUp size={14} />
                                        </button>
                                        <span style={{
                                            minWidth: '40px',
                                            textAlign: 'center',
                                            backgroundColor: '#374151',
                                            padding: '4px 8px',
                                            borderRadius: '6px',
                                            fontSize: '0.9em',
                                            fontWeight: '600'
                                        }}>{trackOffsetY}</span>
                                        <button
                                            onClick={() => adjustTrackOffsetY(5)}
                                            style={{
                                                ...modernStyles.button,
                                                ...modernStyles.buttonSmall,
                                                backgroundColor: '#4b5563'
                                            }}
                                            className="button-hover"
                                        >
                                            <ArrowDown size={14} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div style={{ marginBottom: '25px' }}>
                                <h3 style={{
                                    fontSize: '1.2em',
                                    color: '#d1d5db',
                                    marginBottom: '15px',
                                    fontWeight: '600'
                                }}>{t('visualizationSettings')}</h3>

                                <div style={{
                                    marginBottom: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    flexWrap: 'wrap'
                                }}>
                                    <span style={{
                                        minWidth: '120px',
                                        textAlign: 'right',
                                        fontSize: '0.95em',
                                        color: '#d1d5db',
                                        fontWeight: '500'
                                    }}>{t('trackVisualization')}</span>
                                    <select
                                        value={trackVisualization}
                                        onChange={(e) => setTrackVisualization(e.target.value)}
                                        style={{
                                            ...modernStyles.input,
                                            flexGrow: 1,
                                            maxWidth: '200px'
                                        }}
                                    >
                                        <option value="none">{t('uniform')}</option>
                                        <option value="speed">{t('speed')}</option>
                                        <option value="elevation">{t('elevation')}</option>
                                        <option value="heartRate">{t('heartRate')}</option>
                                        <option value="cadence">{t('cadence')}</option>
                                    </select>
                                </div>

                                <div style={{
                                    marginBottom: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    flexWrap: 'wrap'
                                }}>
                                    <span style={{
                                        minWidth: '120px',
                                        textAlign: 'right',
                                        fontSize: '0.95em',
                                        color: '#d1d5db',
                                        fontWeight: '500'
                                    }}>{t('maxTrackWidth')}</span>
                                    <span style={{
                                        minWidth: '40px',
                                        textAlign: 'center',
                                        backgroundColor: '#374151',
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        fontSize: '0.9em',
                                        fontWeight: '600'
                                    }}>{maxTrackLineWidth}px</span>
                                    <input
                                        type="range"
                                        min="2"
                                        max="20"
                                        step="1"
                                        value={maxTrackLineWidth}
                                        onChange={(e) => setMaxTrackLineWidth(parseInt(e.target.value))}
                                        style={{
                                            '--progress': `${((maxTrackLineWidth - 2) / (20 - 2)) * 100}%`
                                        }}
                                    />
                                </div>

                                <div style={{
                                    marginBottom: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    flexWrap: 'wrap'
                                }}>
                                    <span style={{
                                        minWidth: '120px',
                                        textAlign: 'right',
                                        fontSize: '0.95em',
                                        color: '#d1d5db',
                                        fontWeight: '500'
                                    }}>{t('metrics')}</span>
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '15px',
                                        marginLeft: '12px'
                                    }}>
                                        {['speed', 'elevation', 'heartRate', 'cadence'].map(metric => (
                                            <label
                                                key={metric}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    fontSize: '0.95em',
                                                    color: '#d1d5db',
                                                    fontWeight: '500'
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={showMetrics[metric]}
                                                    onChange={() => toggleMetricDisplay(metric)}
                                                    style={{
                                                        marginRight: '6px',
                                                        transform: 'scale(1.1)'
                                                    }}
                                                />
                                                {t(metric)}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {maxTime > 0 && (
                        <div style={{ marginTop: '20px' }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '15px'
                            }}>
                                <span style={{
                                    fontSize: '14px',
                                    color: '#9ca3af',
                                    fontWeight: '500',
                                    minWidth: '65px'
                                }}>{formatTime(currentTime)}</span>
                                <div
                                    onClick={handleTimelineUiClick}
                                    style={{
                                        flexGrow: 1,
                                        backgroundColor: '#374151',
                                        borderRadius: '8px',
                                        height: '16px',
                                        cursor: 'pointer',
                                        padding: '2px',
                                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)'
                                    }}
                                >
                                    <div style={{
                                        width: maxTime > 0 ? `${(currentTime / maxTime) * 100}%` : '0%',
                                        height: '100%',
                                        background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
                                        borderRadius: '6px',
                                        transition: 'width 0.1s ease',
                                        boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
                                    }} />
                                </div>
                                <span style={{
                                    fontSize: '14px',
                                    color: '#9ca3af',
                                    fontWeight: '500',
                                    minWidth: '65px'
                                }}>{formatTime(maxTime)}</span>
                            </div>
                        </div>
                    )}
                </div>

                <div style={{
                    ...modernStyles.card,
                    ...modernStyles.cardGradient,
                    padding: '0',
                    overflow: 'hidden'
                }} className="slide-up">
                    <canvas
                        ref={canvasRef}
                        width={1200}
                        height={800}
                        style={{
                            display: 'block',
                            width: '100%',
                            height: 'auto',
                            borderRadius: '12px'
                        }}
                    />
                </div>

                {tracks.length > 0 && (
                    <button
                        onClick={togglePlayback}
                        style={{
                            ...modernStyles.floatingButton,
                            background: isPlaying
                                ? 'linear-gradient(135deg, #dc2626, #b91c1c)'
                                : 'linear-gradient(135deg, #059669, #047857)',
                            transform: isPlaying ? 'scale(1.1)' : 'scale(1)'
                        }}
                        className="button-hover"
                    >
                        {isPlaying ? <Pause size={24} /> : <Play size={24} />}
                    </button>
                )}

                {showAvatarModal && editingTrackIndex !== null && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.8)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1000,
                            backdropFilter: 'blur(8px)'
                        }}
                        onClick={() => setShowAvatarModal(false)}
                        className="fade-in"
                    >
                        <div
                            style={{
                                ...modernStyles.card,
                                ...modernStyles.cardGradient,
                                minWidth: '400px',
                                maxWidth: '600px',
                                maxHeight: '80vh',
                                overflowY: 'auto',
                                margin: '20px',
                                border: '1px solid #4b5563'
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="slide-up"
                        >
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '20px',
                                borderBottom: '1px solid #4b5563',
                                paddingBottom: '15px'
                            }}>
                                <h3 style={{
                                    fontSize: '1.3em',
                                    color: '#f8fafc',
                                    margin: 0,
                                    fontWeight: '600'
                                }}>
                                    {t('editAvatarTooltip')} - {tracks[editingTrackIndex]?.name}
                                </h3>
                                <button
                                    onClick={() => setShowAvatarModal(false)}
                                    style={{
                                        ...modernStyles.button,
                                        ...modernStyles.buttonSmall,
                                        backgroundColor: 'transparent',
                                        border: '1px solid #4b5563'
                                    }}
                                    className="button-hover"
                                >
                                    <XCircle size={18}/>
                                </button>
                            </div>

                            <div style={{
                                display: 'flex',
                                gap: '12px',
                                marginBottom: '20px'
                            }}>
                                <button
                                    onClick={() => setAvatarModalMode('emoji')}
                                    style={{
                                        ...modernStyles.button,
                                        flexGrow: 1,
                                        backgroundColor: avatarModalMode === 'emoji' ? '#3b82f6' : '#374151',
                                        border: avatarModalMode === 'emoji' ? '2px solid #60a5fa' : '1px solid #4b5563'
                                    }}
                                    className="button-hover"
                                >
                                    <Smile size={18} />
                                    {t('chooseEmoji')}
                                </button>
                                <button
                                    onClick={() => setAvatarModalMode('image')}
                                    style={{
                                        ...modernStyles.button,
                                        flexGrow: 1,
                                        backgroundColor: avatarModalMode === 'image' ? '#3b82f6' : '#374151',
                                        border: avatarModalMode === 'image' ? '2px solid #60a5fa' : '1px solid #4b5563'
                                    }}
                                    className="button-hover"
                                >
                                    <ImageIcon size={18} />
                                    {t('uploadImage')}
                                </button>
                            </div>

                            {avatarModalMode === 'emoji' && (
                                <>
                                    <p style={{
                                        fontSize: '0.95em',
                                        color: '#9ca3af',
                                        textAlign: 'center',
                                        marginBottom: '15px'
                                    }}>
                                        {t('selectAnEmoji')}
                                    </p>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))',
                                        gap: '12px',
                                        marginBottom: '20px'
                                    }}>
                                        {defaultAvatarsEmojiList.map(emoji => (
                                            <button
                                                key={emoji}
                                                onClick={() => handleSelectEmoji(emoji)}
                                                style={{
                                                    fontSize: '28px',
                                                    cursor: 'pointer',
                                                    background: 'rgba(59, 130, 246, 0.1)',
                                                    border: '1px solid rgba(59, 130, 246, 0.3)',
                                                    padding: '12px',
                                                    borderRadius: '8px',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                title={emoji}
                                                className="button-hover"
                                            >
                                                {emoji}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}

                            {avatarModalMode === 'image' && (
                                <>
                                    <div
                                        style={{
                                            border: '2px dashed #4b5563',
                                            borderRadius: '12px',
                                            padding: '30px',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            marginBottom: '20px',
                                            backgroundColor: 'rgba(59, 130, 246, 0.05)',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onClick={() => fileInputRef.current?.click()} // This ref is for avatar image input
                                        className="button-hover"
                                    >
                                        <ImageIcon size={48} color="#60a5fa" style={{marginBottom: '15px'}}/>
                                        <p style={{
                                            margin: '0 0 8px 0',
                                            fontSize: '1em',
                                            color: '#d1d5db',
                                            fontWeight: '500'
                                        }}>
                                            {t('uploadInstructions')}
                                        </p>
                                        <p style={{
                                            margin: 0,
                                            fontSize: '0.85em',
                                            color: '#9ca3af'
                                        }}>
                                            {t('pasteImageSupport')}
                                        </p>
                                    </div>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={fileInputRef} // This ref is specifically for the avatar image upload
                                        onChange={handleImageFileSelected}
                                        style={{display: 'none'}}
                                    />
                                    {tracks[editingTrackIndex]?.avatarImageSrc && (
                                        <div style={{
                                            textAlign: 'center',
                                            marginTop: '15px',
                                            marginBottom: '15px'
                                        }}>
                                            <p style={{
                                                fontSize: '0.9em',
                                                color: '#9ca3af',
                                                marginBottom: '8px'
                                            }}>
                                                {t('currentAvatar')}
                                            </p>
                                            <img
                                                src={tracks[editingTrackIndex].avatarImageSrc}
                                                alt="current avatar"
                                                style={{
                                                    maxWidth: '120px',
                                                    maxHeight: '120px',
                                                    borderRadius: '8px',
                                                    border: '2px solid #4b5563',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        </div>
                                    )}
                                </>
                            )}

                            <button
                                onClick={handleRemoveCustomAvatar}
                                style={{
                                    ...modernStyles.button,
                                    background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                                    width: '100%',
                                    marginTop: '20px',
                                    fontSize: '1em'
                                }}
                                className="button-hover"
                            >
                                <XCircle size={18} />
                                {t('removeCustomAvatar')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default TrackVisualizer;